// 사용자 행동 자동 추적기 — 화면 이동(pageview)·클릭·제출·연타(rage_click)를 남긴다.
//
// BrowserRouter·AuthProvider 안쪽에 한 번만 마운트한다(App.tsx). 로그인한 사용자의 행동만 기록되도록
// useAuth 로 user 존재를 확인한다(비로그인 공개 폼 화면은 기록하지 않는다). 실제 전송·묶음·세션 시작은
// lib/activity 가 맡고, 여기선 "언제·어디서·무엇을" 을 판단해 넘긴다.
//
// 클릭·제출은 문서 전체에 한 번 건 리스너(capture 단계)가 잡는다 — 버튼마다 track() 을 심지 않아도
// 새 화면이 저절로 수집된다(드림 방식, 2026-10-05). 이름은 lib/activityName 의 «구역 · 행동» 규칙.
// 🔒 입력값은 보내지 않는다 — 입력칸을 눌러도 «검색창»·«입력칸» 같은 종류 이름만 남는다.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { resetActivitySession, track, trackPageview } from "../../lib/activity";
import { actionName, normalizeActivityPath } from "../../lib/activityName";

// 눌렀다고 볼 요소 — 안쪽 아이콘(svg·span)을 눌러도 바깥 버튼 이름으로 잡는다.
const CLICKABLE =
  'button,a,summary,input,select,textarea,label,[role="button"],[role="tab"],[role="checkbox"],[role="menuitem"],[role="switch"],[data-activity-name]';

// 연타 판정 — 같은 대상을 RAGE_WINDOW_MS 안에 RAGE_COUNT 번 이상 누르면 한 번 기록한다.
const RAGE_WINDOW_MS = 1000;
const RAGE_COUNT = 3;

// 경로 → 사람이 읽는 페이지 이름. 대시보드 표시·집계 라벨로 쓴다. 없으면 경로가 그대로 라벨이 된다.
const PAGE_LABELS: Record<string, string> = {
  "/": "홈",
  "/crm/customers": "고객 목록",
  "/crm/customers/:id": "고객 상세",
  "/crm/prospects": "가망고객",
  "/crm/applications/:id": "신청서 상세",
  "/crm/form-templates": "서류 자동작성",
  "/crm/form-templates/:id": "서식 상세",
  "/crm/dealer-templates": "대리점 템플릿",
  "/crm/customer-form-settings": "입력폼 설정",
  "/crm/import": "데이터 가져오기",
  "/crm/restore": "데이터 복원",
  "/inventory/devices": "재고 단말",
  "/inventory/holdings": "보유 재고",
  "/kakao-counsel": "상담 인박스",
  "/kakao-counsel/training": "챗봇 훈련장",
  "/kakao-counsel/learned": "챗봇이 배운 것",
  "/policy/upload": "정책 업로드",
  "/policy/catalog": "데이터 카탈로그",
  "/policy/dev/anomalies": "파싱이상 콘솔",
  "/admin/users": "회원 관리",
  "/admin/data-health": "데이터 헬스",
  "/admin/tenants": "매장 관리",
  "/admin/store-members": "매장별 권한",
  "/admin/crawling": "크롤링 관리",
  "/admin/inbound-messages": "수신 메시지",
  "/admin/activity": "활동 로그",
  "/admin/page-inventory": "페이지 인벤토리",
  "/store/staff": "직원 관리",
  "/store/child-stores": "하위 매장",
};

/** 지금 화면의 경로·이름 — 클릭이 어느 화면에서 났는지 붙인다. */
function here(): { path: string; pageLabel: string | null } {
  const path = normalizeActivityPath(window.location.pathname);
  return { path, pageLabel: PAGE_LABELS[path] ?? null };
}

export default function ActivityTracker() {
  const location = useLocation();
  const { user } = useAuth();
  const lastUserId = useRef<string | null>(null);
  const userId = user?.id ?? null;

  // 다른 사람으로 다시 로그인하면 세션을 끊는다(같은 탭이라도 앞사람 세션에 섞이지 않게).
  useEffect(() => {
    if (userId && lastUserId.current && lastUserId.current !== userId) resetActivitySession();
    if (userId) lastUserId.current = userId;
  }, [userId]);

  useEffect(() => {
    if (!user) return; // 비로그인 이동은 기록하지 않는다.
    const path = normalizeActivityPath(location.pathname);
    trackPageview(path, PAGE_LABELS[path] ?? null);
    // pathname 이 바뀔 때만(쿼리스트링 변화는 무시) 한 번 기록.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.id]);

  // 전역 클릭·제출 — 로그인 동안만 듣는다.
  useEffect(() => {
    if (!userId) return;
    let rageEl: Element | null = null;
    let rageTimes: number[] = [];
    let rageFired = false;

    const onClick = (e: MouseEvent) => {
      if (!e.isTrusted || !(e.target instanceof Element)) return;
      const el = e.target.closest(CLICKABLE) ?? e.target;
      // label 을 누르면 브라우저가 연결된 체크 상자에 클릭을 한 번 더 쏜다(detail=0) — 사람 손이 아니라 뺀다.
      if (el instanceof HTMLInputElement && e.detail === 0 && (el.type === "checkbox" || el.type === "radio")) return;
      const name = actionName(el);
      track("click", { ...here(), target: name });

      // 연타 — 같은 요소를 1초 안에 3번 이상. 한 번 몰아치는 동안엔 한 건만 남긴다.
      const now = Date.now();
      if (el !== rageEl) {
        rageEl = el;
        rageTimes = [];
        rageFired = false;
      }
      rageTimes = [...rageTimes.filter((t) => now - t <= RAGE_WINDOW_MS), now];
      if (rageTimes.length < 2) rageFired = false; // 1초 넘게 쉬었으면 새 몰아치기
      if (!rageFired && rageTimes.length >= RAGE_COUNT) {
        rageFired = true;
        track("rage_click", { ...here(), target: name, meta: { count: rageTimes.length, windowMs: RAGE_WINDOW_MS } });
      }
    };

    const onSubmit = (e: SubmitEvent) => {
      if (!e.isTrusted || !(e.target instanceof Element)) return;
      track("submit", { ...here(), target: actionName(e.target) });
    };

    document.addEventListener("click", onClick, true);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, [userId]);

  return null;
}
